import { File, FileText, FileImage, FileArchive } from 'lucide-react'
import type { MailMessageActionState } from 'hooks/useMailMessageActions'
import { Button } from '../ui/button'
import type { MailAttachment, LogicalMailAttachment } from '../../services/mail'

// Presentation model, not an API response: a future adapter supplies authorized capabilities.
export interface MailAttachmentPresentation {
  attachment: MailAttachment | LogicalMailAttachment
  download: boolean
  exportDocument: boolean
  unavailableReason?: string
}

export function formatMailAttachmentSize(bytes: number): string {
  if (bytes === 0) return '0 KB'
  if (bytes < 1024) return '<1 KB'
  const megabytes = bytes >= 1024 * 1024
  return `${new Intl.NumberFormat('it-IT', { maximumFractionDigits: 1 }).format(bytes / (megabytes ? 1024 * 1024 : 1024))} ${megabytes ? 'MB' : 'KB'}`
}
interface Props {
  states?: Record<string, MailMessageActionState>
  logicalMessageId: string
  sourceId: string
  items: MailAttachmentPresentation[]
  onDownload?: (logicalMessageId: string, sourceId: string, attachmentId: string) => void
  onExport?: (logicalMessageId: string, sourceId: string, attachmentId: string) => void
}

export function MailMessageAttachments({ states = {}, logicalMessageId, sourceId, items, onDownload, onExport }: Props) {
  if (!items.length) return null
  return <section className="mail-attachments" aria-label={`Allegati del messaggio ${logicalMessageId}`}>
    <h4>Allegati</h4>
    <ul className="mail-conversation-attachment-list">
      {items.map(({ attachment, download, exportDocument, unavailableReason }) => {
        const Icon = attachment.content_type?.startsWith('image/') ? FileImage : attachment.content_type?.startsWith('text/') || attachment.content_type === 'application/pdf' ? FileText : /zip|compressed|archive/.test(attachment.content_type || '') ? FileArchive : File
        const downloadState = states[`download:${attachment.id}`]
        return <li className="mail-attachment" key={attachment.id}>
        <div className="flex items-center gap-2"><Icon aria-hidden="true" size={22} /><p className="break-words">{attachment.filename || 'Allegato senza nome'}</p></div><p>{formatMailAttachmentSize(attachment.size_bytes)}</p>
        {attachment.availability !== 'complete' && <p>{unavailableReason || (attachment.availability === 'expired' ? 'File scaduto, non disponibile per il download.' : 'File non disponibile completamente. Non può essere scaricato.')}</p>}
        <details><summary>Dettagli file</summary><p>Disponibilità: {attachment.availability}</p>{attachment.excluded_reason && <p>{attachment.excluded_reason}</p>}</details>
        {downloadState?.status === 'loading' && <p role="status">Download in corso…</p>}
        {downloadState?.status === 'error' && <p role="alert">Download non riuscito. Riprova.</p>}
        {downloadState?.status === 'ready' && <p role="status">Download avviato.</p>}
        <div className="mail-conversation-actions">
          {attachment.availability === 'complete' && download && onDownload && <Button type="button" disabled={downloadState?.status === 'loading'}
            aria-label={`Scarica ${attachment.filename || 'Allegato senza nome'} (${logicalMessageId}, ${attachment.id})`}
            onClick={() => onDownload(logicalMessageId, sourceId, attachment.id)}>Scarica</Button>}
          {attachment.availability === 'complete' && exportDocument && onExport && <Button type="button" variant="outline"
            aria-label={`Salva nei Documenti ${attachment.filename || 'Allegato senza nome'} (${logicalMessageId}, ${attachment.id})`}
            onClick={() => onExport(logicalMessageId, sourceId, attachment.id)}>Salva nei Documenti</Button>}
        </div>
      </li>})}
    </ul>
  </section>
}
